// @ts-check

import { openDB } from "idb";

const DB_NAME = "brawnyai-sudoku";
const DB_VERSION = 1;
const STORES = [
  "puzzles",
  "puzzle_packs",
  "collection_items",
  "sessions",
  "completed_games",
  "daily_results",
  "unlocks",
  "statistics",
  "settings",
  "migrations",
];

export class SaveStore {
  constructor() {
    this.dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, _newVersion, transaction) {
        for (const name of STORES)
          if (!db.objectStoreNames.contains(name)) db.createObjectStore(name);
        if (oldVersion < 1)
          transaction
            .objectStore("migrations")
            .put({ applied_at: new Date().toISOString() }, "schema_1");
      },
    });
  }
  async seedCatalog(catalog) {
    const db = await this.dbPromise;
    const transaction = db.transaction(
      ["puzzles", "puzzle_packs", "collection_items"],
      "readwrite",
    );
    await Promise.all([
      ...catalog.puzzles.map((puzzle) =>
        transaction.objectStore("puzzles").put(puzzle, puzzle.id),
      ),
      ...catalog.packs.map((pack) =>
        transaction.objectStore("puzzle_packs").put(pack, pack.id),
      ),
      ...catalog.collection_items.map((item) =>
        transaction.objectStore("collection_items").put(item, item.id),
      ),
      transaction.done,
    ]);
  }
  async get(store, key) {
    return (await this.dbPromise).get(store, key);
  }
  async put(store, key, value) {
    return (await this.dbPromise).put(store, structuredClone(value), key);
  }
  async all(store) {
    return (await this.dbPromise).getAll(store);
  }
  async delete(store, key) {
    return (await this.dbPromise).delete(store, key);
  }
  async clear(store) {
    return (await this.dbPromise).clear(store);
  }
  async loadActive() {
    try {
      const record = await this.get("sessions", "active");
      if (
        !record ||
        record.state_version !== 1 ||
        typeof record.puzzle_id !== "string" ||
        record.values?.length !== 81
      )
        return null;
      return record;
    } catch (error) {
      console.warn("Ignoring corrupt active session", error);
      return null;
    }
  }
  async saveActive(record) {
    return this.put("sessions", "active", {
      ...record,
      state_version: 1,
      saved_at: new Date().toISOString(),
    });
  }
  async clearActive() {
    return this.delete("sessions", "active");
  }
  async archiveCompletion(record) {
    const db = await this.dbPromise;
    const id = record.id ?? `${record.puzzle_id}:${record.completed_at}`;
    const transaction = db.transaction(
      ["completed_games", "unlocks", "daily_results"],
      "readwrite",
    );
    await transaction.objectStore("completed_games").put({ ...record, id }, id);
    await transaction.objectStore("unlocks").put(
      {
        collection_item_id: record.collection_item_id,
        unlocked_at: record.completed_at,
      },
      record.collection_item_id,
    );
    if (record.daily_date)
      await transaction.objectStore("daily_results").put(
        {
          date: record.daily_date,
          puzzle_id: record.puzzle_id,
          completed_at: record.completed_at,
        },
        record.daily_date,
      );
    await transaction.done;
  }
  async settings() {
    return (await this.get("settings", "current")) ?? {};
  }
  async saveSettings(settings) {
    return this.put("settings", "current", settings);
  }
  async resetStatistics() {
    return this.put("statistics", "reset_epoch", {
      at: new Date().toISOString(),
    });
  }
  async statisticsEpoch() {
    return (await this.get("statistics", "reset_epoch"))?.at ?? null;
  }
}
